import { useParams, useRouteLoaderData } from "@remix-run/react";
import { useEffect, useMemo, useState } from "react";
import { Templates as TrustpilotTemplates } from "~/components/TrustpilotWidget";
import { WindowWithENV } from "~/entities/global/types";
import { TrustpilotStructuredData } from "~/entities/structuredData/types";
import { validProductStructuredData } from "~/entities/structuredData/validators";
import { useHydrated } from "~/hooks/useHydrated";
import { loader } from "~/routes/pages/product";

function useStructuredData() {
  const isHydrated = useHydrated();
  const params = useParams();
  const productData = useRouteLoaderData<typeof loader>("product-detail-page");
  const [structuredData, setStructuredData] = useState<unknown>(undefined);
  const [trustpilotReviews, setTrustpilotReviews] = useState<
    TrustpilotStructuredData | undefined
  >(undefined);

  const { entity, id } = useMemo(() => {
    if (!isHydrated || !params) {
      return { entity: undefined, id: undefined };
    }

    if (Object.keys(params).join("/") === "mill/category/slug") {
      return {
        entity: "products",
        id: params.slug?.split("-").shift(),
      };
    }

    return { entity: undefined, id: undefined };
  }, [isHydrated, params]);

  useEffect(
    function getStructuredData() {
      if (!entity || !id || structuredData) return;

      fetch(`/resources/structured-data/${entity}/${id}`)
        .then((response) => response.json())
        .then((data) => setStructuredData(data.data))
        .catch((e) => console.error("Error fetching structured data", e));
    },
    [entity, id, structuredData],
  );

  useEffect(
    function getTrustpilotReviews() {
      if (!isHydrated) return;

      const env = (window as WindowWithENV).ENV;

      if (!env || !productData?.product || trustpilotReviews) return;

      const trustpilotProductUrl = new URL(
        `https://widget.trustpilot.com/data/jsonld/business-unit/${env.TRUSTPILOT_BUSINESS_UNIT_ID}/product`,
      );

      trustpilotProductUrl.searchParams.set(
        "sku",
        productData.product.styleCode,
      );
      trustpilotProductUrl.searchParams.set("numberOfReviews", "10");
      trustpilotProductUrl.searchParams.set(
        "productName",
        productData.product.name,
      );
      trustpilotProductUrl.searchParams.set("language", "en");
      trustpilotProductUrl.searchParams.set(
        "templateId",
        TrustpilotTemplates.large,
      );
      trustpilotProductUrl.searchParams.set(
        "url",
        `https://www.shirtspace.com${productData.product.path}`,
      );

      fetch(trustpilotProductUrl.toString())
        .then((response) => response.json())
        .then((data) => {
          setTrustpilotReviews({
            review: data.review,
            aggregateRating: data.aggregateRating,
          });
        })
        .catch((e) => console.error("Error fetching Trustpilot reviews", e));
    },
    [isHydrated, productData?.product, trustpilotReviews],
  );

  return useMemo(() => {
    const productStructuredData = validProductStructuredData(structuredData);

    if (productStructuredData) {
      return {
        productGroup: {
          ...productStructuredData.productGroup,
          review: trustpilotReviews?.review,
          aggregateRating: trustpilotReviews?.aggregateRating,
        },
        productSnippet: {
          ...productStructuredData.productSnippet,
          review: trustpilotReviews?.review,
          aggregateRating: trustpilotReviews?.aggregateRating,
        },
      };
    }

    return undefined;
  }, [structuredData, trustpilotReviews]);
}

export default useStructuredData;
